import React, { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { callActions } from "../../../../core/data/redux/live-call/slice";
import { acquireCallLock, getCallSocket } from "../services/callSocket";
import { manualEndCallRef } from "../services/callSessionRefs";
import type { AppDispatch } from "../../../../core/data/redux/store";

import CallScreen from "../callScreen";
import CallOutcomeModal from "./CallOutcomeModal";
import LogCallLeadModal from "./LogCallLeadModal";
import AddLeadToZyvo from "./AddLeadToZyvo";
import { useGlobalCallRefs } from "../hooks/useGlobalCallRefs";

const useCallState = () => useSelector((s: any) => s.liveCall);
const useUser = () => useSelector((s: any) => s.user?.user);

export default function GlobalCallOverlay() {
    const dispatch = useDispatch<AppDispatch>();
    const call = useCallState();
    const user = useUser();
    const signature = localStorage.getItem("signature");

    const { startNextCallRef } = useGlobalCallRefs();
    const afterModal = () => {
        dispatch(callActions.setCallDisconnectedData(null));
        if (call.isAutoCall && !call.autoCallStopped) {
            startNextCallRef.current?.();
        } else {
            // Refresh calls after form submission when not in auto call
            window.dispatchEvent(new CustomEvent('refreshCallsAfterSubmission'));
        }
    };

    const handleEndCall = () => {
        if (!call.activeCall) return;
        const snapshot = call.activeCall;
        manualEndCallRef.current = true;

        getCallSocket().emit("endCall", {
            userId: user?.userDetails?.id,
            number: snapshot?.mobileNumber,
        });

        dispatch(callActions.setConnected(false));
        dispatch(callActions.setActiveCall(null));
        dispatch(callActions.setCallDisconnectedData(snapshot));

        if (call.isLeadsMode) {
            dispatch(callActions.setShowLeadMgmtOutcomeModal(true));
        } else {
            dispatch(callActions.setShowReferencePopup(true));
        }
    };

    // ── Redial: close modal and re-call the same number 
    const handleRedial = () => {
        const record = call.callDisconnectedData;
        if (!record) return;

        dispatch(callActions.setShowReferencePopup(false));
        dispatch(callActions.setShowLeadMgmtOutcomeModal(false));

        acquireCallLock(user?.userDetails?.id);

        getCallSocket().emit("startCall", {
            userId: user?.userDetails?.id,
            data: record,
        });

        dispatch(callActions.setActiveCall(record));
        dispatch(callActions.setCallDisconnectedData(null));
        dispatch(callActions.setRedialRequested(true));
    };

    return (
        <>
            {call.rejectedMessage && (
                <div
                    style={{ position: "fixed", top: 20, right: 20, zIndex: 9999 }}
                    className="alert alert-danger"
                >
                    {call.rejectedMessage}
                </div>
            )}

            {call.activeCall && (
                <CallScreen
                    record={call.activeCall}
                    statusCall={call.connected}
                    isAutoCall={call.isAutoCall}
                    onEndCall={handleEndCall}
                    onAutoCallToggle={(stopped: boolean) => {
                        dispatch(callActions.setAutoCallStopped(stopped));
                    }}
                />
            )}

            <CallOutcomeModal
                open={call.showReferencePopup}
                callData={call.callDisconnectedData}
                signature={signature}
                onSaveAndNext={() => {
                    dispatch(callActions.setShowReferencePopup(false));
                    afterModal();
                }}
                onSaveAndAddToZyvo={() => {
                    dispatch(callActions.setShowReferencePopup(false));
                    dispatch(callActions.setShowAddToZyvo(true));
                }}
                onRedial={handleRedial}
            />

            {call.showAddToZyvo && (
                <AddLeadToZyvo
                    open={call.showAddToZyvo}
                    callData={call.callDisconnectedData}
                    selectedCompany={call.selectedCompany ?? { companyId: "", companyName: "", companyType: "" }}
                    signature={signature}
                    user={user}
                    onSuccess={() => {
                        dispatch(callActions.setShowAddToZyvo(false));
                        afterModal();
                    }}
                    onSkip={() => {
                        dispatch(callActions.setShowAddToZyvo(false));
                        afterModal();
                    }}
                />
            )}

            <LogCallLeadModal
                open={call.showLeadMgmtOutcomeModal}
                callData={call.callDisconnectedData}
                signature={signature}
                onSuccess={() => {
                    dispatch(callActions.setShowLeadMgmtOutcomeModal(false));
                    afterModal();
                }}
                onRedial={handleRedial}
            />
        </>
    );
}
